import axios from "axios";
import api from "./api";

const PDOK_API_URL = "https://api.pdok.nl/bzk/locatieserver/search/v3_1";
const BAG_API_URL =
  process.env.REACT_APP_BAG_API_URL ||
  "https://api.bag.kadaster.nl/lvbag/individuelebevragingen/v2";
const BAG_API_KEY = String(process.env.REACT_APP_BAG_API_KEY || "").trim();

const pdokApi = axios.create({
  baseURL: PDOK_API_URL,
  headers: { "Content-Type": "application/json" },
});

const bagApi = axios.create({
  baseURL: BAG_API_URL,
  headers: {
    "Content-Type": "application/json",
    Accept: "application/hal+json, application/json",
    "Accept-Crs": "epsg:28992",
    ...(BAG_API_KEY ? { "X-Api-Key": BAG_API_KEY } : {}),
  },
});

async function getWithFallback(primaryPath, fallbackPath, config = {}) {
  try {
    return await api.get(primaryPath, config);
  } catch (error) {
    const status = Number(error?.response?.status || 0);
    if (!fallbackPath || (status && status !== 404)) {
      throw error;
    }
    return api.get(fallbackPath, config);
  }
}

async function postWithFallback(primaryPath, fallbackPath, payload, config = {}) {
  try {
    return await api.post(primaryPath, payload, config);
  } catch (error) {
    const status = Number(error?.response?.status || 0);
    if (!fallbackPath || (status && status !== 404)) {
      throw error;
    }
    return api.post(fallbackPath, payload, config);
  }
}

async function deleteWithFallback(primaryPath, fallbackPath, config = {}) {
  try {
    return await api.delete(primaryPath, config);
  } catch (error) {
    const status = Number(error?.response?.status || 0);
    if (!fallbackPath || (status && status !== 404)) {
      throw error;
    }
    return api.delete(fallbackPath, config);
  }
}

function shouldUseClientFallback(error) {
  const status = error?.response?.status;
  return !status || status === 404 || status === 405 || status >= 500;
}

function mapPdokSuggestResult(doc) {
  return {
    id: doc?.id || "",
    omschrijving: doc?.weergavenaam || "",
    type: doc?.type || "",
  };
}

function formatPdokAddress(doc) {
  if (!doc) {
    return "";
  }

  const street = [doc.straatnaam, doc.huis_nlt].filter(Boolean).join(" ").trim();
  return [street, doc.postcode, doc.woonplaatsnaam].filter(Boolean).join(", ");
}

function mapPdokAddressDetail(doc) {
  if (!doc) {
    return null;
  }

  return {
    id: doc.id || null,
    formattedAddress: formatPdokAddress(doc) || doc.weergavenaam || "",
    nummeraanduidingIdentificatie: doc.nummeraanduiding_id || null,
    adresseerbaarObjectIdentificatie: doc.adresseerbaarobject_id || null,
    woonplaatsNaam: doc.woonplaatsnaam || "",
    postcode: doc.postcode || "",
    straatnaam: doc.straatnaam || "",
    huisnummer: doc.huisnummer || null,
    pandIdentificaties: [],
    source: "pdok",
  };
}

function mapBagAddressDetail(address) {
  if (!address) {
    return null;
  }

  const adresseerbaarObject =
    address.adresseerbaarObject ||
    address._embedded?.adresseerbaarObject ||
    address._embedded?.adresseerbaarobject ||
    {};
  const openbareruimte = address.openbareRuimteNaam || address.openbareruimteNaam || "";
  const huisnummer = address.huisnummer || "";
  const huisletter = address.huisletter || "";
  const huisnummertoevoeging = address.huisnummertoevoeging || "";
  const postcode = address.postcode || "";
  const woonplaats =
    address.woonplaatsNaam ||
    address.woonplaats?.naam ||
    address.woonplaats ||
    "";
  const street = [openbareruimte, `${huisnummer}${huisletter}${huisnummertoevoeging}`.trim()]
    .filter(Boolean)
    .join(" ")
    .trim();
  const rawPandIdentificaties =
    address.pandIdentificaties ||
    address.pandIdentificatie ||
    adresseerbaarObject.pandIdentificaties ||
    adresseerbaarObject.pandIdentificatie ||
    [];
  const rawGebruiksdoelen = address.gebruiksdoelen || adresseerbaarObject.gebruiksdoelen || [];
  const rawOppervlakte = address.oppervlakte ?? adresseerbaarObject.oppervlakte;
  const rawBouwjaar = address.oorspronkelijkBouwjaar || adresseerbaarObject.oorspronkelijkBouwjaar || [];
  const oppervlakte =
    rawOppervlakte === null || rawOppervlakte === undefined || rawOppervlakte === ""
      ? null
      : Number(rawOppervlakte);

  return {
    ...address,
    formattedAddress:
      address.formattedAddress || [street, postcode, woonplaats].filter(Boolean).join(", "),
    nummeraanduidingIdentificatie:
      address.nummeraanduidingIdentificatie || address.identificatie || null,
    adresseerbaarObjectIdentificatie:
      address.adresseerbaarObjectIdentificatie || address.adresseerbaarObject?.identificatie || null,
    woonplaatsNaam: woonplaats,
    pandIdentificaties: Array.isArray(rawPandIdentificaties)
      ? rawPandIdentificaties.map((value) => String(value || "").trim()).filter(Boolean)
      : address.panden?.map((pand) => pand.identificatie).filter(Boolean) || [],
    gebruiksdoelen: Array.isArray(rawGebruiksdoelen)
      ? rawGebruiksdoelen.map((value) => String(value || "").trim()).filter(Boolean)
      : [],
    oppervlakte: Number.isFinite(oppervlakte) ? oppervlakte : null,
    typeAdresseerbaarObject: String(
      address.typeAdresseerbaarObject || adresseerbaarObject.typeAdresseerbaarObject || ""
    ).trim(),
    oorspronkelijkBouwjaar: Array.isArray(rawBouwjaar)
      ? rawBouwjaar.map((value) => String(value || "").trim()).filter(Boolean)
      : [],
    source: "bag",
  };
}

async function searchBagAddressesViaPdok(query) {
  const res = await pdokApi.get("/suggest", {
    params: { q: query, wt: "json" },
  });

  return (res?.data?.response?.docs || [])
    .filter((doc) => doc?.type === "adres")
    .map(mapPdokSuggestResult);
}

async function getBagAddressDetailViaPdok(id) {
  const res = await pdokApi.get("/lookup", {
    params: { id, wt: "json" },
  });

  return mapPdokAddressDetail(res?.data?.response?.docs?.[0] || null);
}

async function getBagAddressDetailViaKadaster(nummeraanduidingIdentificatie) {
  if (!BAG_API_KEY) {
    return null;
  }

  let addressPayload = null;
  try {
    const res = await bagApi.get(`/adressenuitgebreid/${encodeURIComponent(nummeraanduidingIdentificatie)}`);
    addressPayload = res?.data || null;
  } catch (error) {
    if (error?.response?.status && ![404, 412].includes(Number(error.response.status))) {
      throw error;
    }
  }

  if (!addressPayload) {
    const fallbackRes = await bagApi.get(`/adressen/${encodeURIComponent(nummeraanduidingIdentificatie)}`);
    addressPayload = fallbackRes?.data || null;
  }

  let mapped = mapBagAddressDetail(addressPayload);
  const hasGebruiksdoelen = Array.isArray(mapped?.gebruiksdoelen) && mapped.gebruiksdoelen.length > 0;
  const hasOppervlakte = Number.isFinite(Number(mapped?.oppervlakte));
  const adresseerbaarObjectId = String(mapped?.adresseerbaarObjectIdentificatie || "").trim();

  if ((!hasGebruiksdoelen || !hasOppervlakte) && adresseerbaarObjectId) {
    try {
      const aoRes = await bagApi.get(`/adresseerbareobjecten/${encodeURIComponent(adresseerbaarObjectId)}`);
      const enriched = mapBagAddressDetail({
        ...(addressPayload || {}),
        adresseerbaarObject: aoRes?.data || {},
        adresseerbaarObjectIdentificatie: adresseerbaarObjectId,
      });
      mapped = {
        ...mapped,
        ...enriched,
        gebruiksdoelen:
          Array.isArray(enriched?.gebruiksdoelen) && enriched.gebruiksdoelen.length > 0
            ? enriched.gebruiksdoelen
            : mapped?.gebruiksdoelen || [],
        oppervlakte:
          Number.isFinite(Number(enriched?.oppervlakte)) ? Number(enriched.oppervlakte) : mapped?.oppervlakte ?? null,
      };
    } catch (error) {
      if (error?.response?.status && Number(error.response.status) !== 404) {
        throw error;
      }
    }
  }

  return mapped;
}

export async function getFireSafetyModuleSettings() {
  const res = await api.get("/tenant/settings");
  return res?.data?.modules?.fireSafetyLogbook || { enabled: false };
}

export async function updateFireSafetyModuleSettings(enabled) {
  const res = await api.put("/tenant/settings/modules", {
    fireSafetyLogbook: { enabled: Boolean(enabled) },
  });
  return res?.data?.modules?.fireSafetyLogbook || { enabled: Boolean(enabled) };
}

export async function getInstallations(query = "") {
  const params =
    typeof query === "object" && query !== null
      ? query
      : query
        ? { q: query }
        : {};
  const res = await getWithFallback("/installations", "/fire-logbook/installations", {
    params,
  });
  return res?.data?.installations || [];
}

export async function getInstallation(id) {
  const res = await getWithFallback(`/installations/${id}`, `/fire-logbook/installations/${id}`);
  return res?.data || {};
}

export async function getInstallationByQrToken(qrToken) {
  const res = await api.get(`/fire-logbook/installations/qr/${qrToken}`);
  return res?.data?.installation || null;
}

export async function createInstallation(payload) {
  const res = await postWithFallback("/installations", "/fire-logbook/installations", payload);
  return res?.data?.installation;
}

export async function deleteInstallation(id) {
  await deleteWithFallback(`/installations/${id}`, `/fire-logbook/installations/${id}`);
}

export async function searchBagAddresses(query) {
  const normalizedQuery = String(query || "").trim();

  if (!normalizedQuery) {
    return [];
  }

  try {
    const res = await api.get("/fire-logbook/bag/adressen/zoek", {
      params: { q: normalizedQuery },
    });
    return res?.data?.results || [];
  } catch (error) {
    if (!shouldUseClientFallback(error)) {
      throw error;
    }

    return searchBagAddressesViaPdok(normalizedQuery);
  }
}

export async function getBagAddressDetail(nummeraanduidingIdentificatie) {
  const detailId = String(nummeraanduidingIdentificatie || "").trim();

  if (!detailId) {
    return null;
  }

  // PDOK suggest ids like "adr-..." are not valid BAG nummeraanduiding ids.
  // Resolve them client-side first and only call BAG detail APIs with the real BAG id.
  if (!detailId.startsWith("adr-")) {
    try {
      const res = await api.get(`/fire-logbook/bag/adressen/${encodeURIComponent(detailId)}`);
      return res?.data?.address || null;
    } catch (error) {
      if (!shouldUseClientFallback(error)) {
        throw error;
      }
    }
  }

  if (!detailId.startsWith("adr-")) {
    try {
      const bagAddress = await getBagAddressDetailViaKadaster(detailId);
      if (bagAddress) {
        return bagAddress;
      }
    } catch {
      // Val door naar PDOK fallback als directe BAG detail lookup faalt.
    }
  }

  const pdokAddress = await getBagAddressDetailViaPdok(detailId);
  const nummeraanduidingId = pdokAddress?.nummeraanduidingIdentificatie || detailId;

  if (!nummeraanduidingId) {
    return pdokAddress;
  }

  try {
    const bagAddress = await getBagAddressDetailViaKadaster(nummeraanduidingId);
    if (!bagAddress) {
      return pdokAddress;
    }

    return {
      ...pdokAddress,
      ...bagAddress,
      pandIdentificaties:
        Array.isArray(bagAddress.pandIdentificaties) && bagAddress.pandIdentificaties.length > 0
          ? bagAddress.pandIdentificaties
          : pdokAddress.pandIdentificaties,
    };
  } catch {
    return pdokAddress;
  }
}

export async function getInstallationLogbook(id) {
  const res = await getWithFallback(
    `/installations/${id}/logbook`,
    `/fire-logbook/installations/${id}/logbook`
  );
  return res?.data?.entries || [];
}

export async function createLogbookEntry(installationId, payload) {
  const formData = new FormData();
  formData.append("entryType", payload.entryType || "");
  formData.append("description", payload.description || "");
  for (const file of payload.attachments || []) {
    formData.append("attachments", file);
  }

  const res = await api.post(
    `/fire-logbook/installations/${installationId}/logbook-entry`,
    formData,
    { headers: { "Content-Type": "multipart/form-data" } }
  );
  return res?.data?.entry;
}

export async function correctLogbookEntry(entryId, payload) {
  const formData = new FormData();
  formData.append("entryType", payload.entryType || "");
  formData.append("description", payload.description || "");
  for (const file of payload.attachments || []) {
    formData.append("attachments", file);
  }

  const res = await api.post(`/fire-logbook/logbook-entry/${entryId}/correct`, formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return res?.data?.entry;
}
